import { useState, useEffect, useCallback } from 'react';
import { Input, Select, Space, AutoComplete, Switch } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { SearchType } from '../../types/product';
import { getManufacturersAutocomplete, getCategoriesAutocomplete } from '../../api/products';
import debounce from 'lodash/debounce';

const { Search } = Input;

interface ProductSearchProps {
  searchType: SearchType;
  onSearchTypeChange: (type: SearchType) => void;
  onSearch: (query: string) => void;
  loading?: boolean;
  defaultValue?: string;
  value?: string;
  onChange?: (value: string) => void;
  exactMatch?: boolean;
  onExactMatchChange?: (exact: boolean) => void;
}

const searchTypeOptions: { value: SearchType; label: string }[] = [
  { value: 'mpn', label: 'MPN' },
  { value: 'product_id', label: 'Product ID' },
  { value: 'sku', label: 'SKU' },
  { value: 'upc', label: 'UPC' },
  { value: 'ingram_sku', label: 'Ingram SKU' },
  { value: 'description', label: 'Description' },
  { value: 'category', label: 'Category' },
  { value: 'manufacturer', label: 'Manufacturer' },
];

export function ProductSearch({
  searchType,
  onSearchTypeChange,
  onSearch,
  loading,
  defaultValue,
  value,
  onChange,
  exactMatch,
  onExactMatchChange,
}: ProductSearchProps) {
  const [options, setOptions] = useState<{ value: string }[]>([]);
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);

  const isAutocompleteType = searchType === 'manufacturer' || searchType === 'category';

  // Debounced function to fetch autocomplete suggestions
  const fetchSuggestions = useCallback(
    debounce(async (searchText: string, type: SearchType) => {
      if (!searchText || searchText.length < 1) {
        setOptions([]);
        return;
      }

      setAutocompleteLoading(true);
      try {
        let results: string[] = [];
        if (type === 'manufacturer') {
          results = await getManufacturersAutocomplete(searchText, 20);
        } else if (type === 'category') {
          results = await getCategoriesAutocomplete(searchText, 20);
        }
        setOptions(results.map((item) => ({ value: item })));
      } catch (error) {
        console.error('Autocomplete fetch failed:', error);
        setOptions([]);
      } finally {
        setAutocompleteLoading(false);
      }
    }, 300),
    []
  );

  // Clear options when search type changes
  useEffect(() => {
    setOptions([]);
  }, [searchType]);

  // Handle autocomplete search
  const handleAutocompleteSearch = (searchText: string) => {
    onChange?.(searchText);
    if (isAutocompleteType) {
      fetchSuggestions(searchText, searchType);
    }
  };

  // Handle selecting an option from autocomplete
  const handleSelect = (selectedValue: string) => {
    onChange?.(selectedValue);
    onSearch(selectedValue);
  };

  // Handle pressing Enter in autocomplete
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && value) {
      onSearch(value);
    }
  };

  const placeholder = `Search by ${searchTypeOptions.find((o) => o.value === searchType)?.label || searchType}...`;

  // Render autocomplete for manufacturer/category
  if (isAutocompleteType) {
    return (
      <Space.Compact style={{ width: '100%', maxWidth: 600 }}>
        <Select
          value={searchType}
          onChange={onSearchTypeChange}
          options={searchTypeOptions}
          style={{ width: 150 }}
          size="large"
        />
        <AutoComplete
          style={{ flex: 1, minWidth: 200 }}
          options={options}
          onSearch={handleAutocompleteSearch}
          onSelect={handleSelect}
          value={value}
          placeholder={placeholder}
          allowClear
          onClear={() => onChange?.('')}
          onKeyDown={handleKeyDown}
        >
          <Input
            size="large"
            suffix={
              <SearchOutlined
                style={{ cursor: 'pointer', color: loading || autocompleteLoading ? '#1890ff' : undefined }}
                onClick={() => value && onSearch(value)}
              />
            }
          />
        </AutoComplete>
      </Space.Compact>
    );
  }

  // Regular search input for other types
  const inputProps = value !== undefined
    ? { value, onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange?.(e.target.value) }
    : { defaultValue };

  return (
    <Space size="middle" align="center" wrap>
      <Space.Compact style={{ width: '100%', maxWidth: 600 }}>
        <Select
          value={searchType}
          onChange={onSearchTypeChange}
          options={searchTypeOptions}
          style={{ width: 150 }}
          size="large"
        />
        <Search
          placeholder={placeholder}
          enterButton={<SearchOutlined />}
          size="large"
          onSearch={onSearch}
          loading={loading}
          allowClear
          style={{ flex: 1 }}
          {...inputProps}
        />
      </Space.Compact>
      {searchType === 'mpn' && onExactMatchChange && (
        <Space size="small" align="center">
          <Switch
            checked={!!exactMatch}
            onChange={onExactMatchChange}
            size="small"
          />
          <span style={{ fontSize: 13 }}>Exact match</span>
        </Space>
      )}
    </Space>
  );
}
